import { useCallback, useState } from 'react'

export const GRANTS_PREFERENCES_STORAGE_KEY = 'preferences_grants'

export type GrantsPreferences = {
  tasksAutosave?: boolean
}

export type UseTasksAutosavePreferenceResult = {
  isAutosaveEnabled: boolean
  shouldShowModal: boolean
  enableAutosave: () => void
  disableAutosave: () => void
}

function readPreferences(): GrantsPreferences {
  try {
    const raw = window.localStorage.getItem(GRANTS_PREFERENCES_STORAGE_KEY)

    return raw ? (JSON.parse(raw) as GrantsPreferences) : {}
  } catch {
    return {}
  }
}

function persistAutosavePreference(value: boolean): void {
  try {
    window.localStorage.setItem(
      GRANTS_PREFERENCES_STORAGE_KEY,
      JSON.stringify({ ...readPreferences(), tasksAutosave: value })
    )
  } catch {
    // Ignore write failures (private mode, quota, SSR).
  }
}

export function useTasksAutosavePreference(
  enabled = true
): UseTasksAutosavePreferenceResult {
  const [preference, setPreference] = useState<boolean | undefined>(
    () => readPreferences().tasksAutosave
  )

  const setAutosave = useCallback((value: boolean) => {
    setPreference(value)
    persistAutosavePreference(value)
  }, [])

  const enableAutosave = useCallback(() => setAutosave(true), [setAutosave])
  const disableAutosave = useCallback(() => setAutosave(false), [setAutosave])

  if (!enabled) {
    return {
      isAutosaveEnabled: false,
      shouldShowModal: false,
      enableAutosave,
      disableAutosave,
    }
  }

  return {
    isAutosaveEnabled: preference ?? false,
    shouldShowModal: preference === undefined,
    enableAutosave,
    disableAutosave,
  }
}
